import { createReducer } from "@reduxjs/toolkit";
import initialState from "./ps-workspace.store";
import { createWorkspace, createWorkspaceFailure, createWorkspaceSuccessful, getMetricTemplateAnnotationsSuccessful, linkTags, linkTagsFailure, loadWorkspace, loadWorkspaceFailure, loadWorkspaceSuccessful, removePrintCanvasContent, removeStoredGraphItem, resetMetricTemplateAnnotations, resultEntityCreatingReset, scenarioEntityCreatingReset, setCalculationHeatmapResults, setCalculationLineGraphResults, setCalculationTabularResults, setPrintCanvasContent, setResultSetElement, setResultSetId, setScenarioEntity, setScenarioId, setSocketError, setSocketStatus, setWorkspaceServerError, subscribeWorkspaceSuccessful, unlinkTags, unlinkTagsFailure, unsubscribeWorkspace, updateWorkspaceLayout, updateWorkspaceName, updateWorkspaceNameFailure, updateWorkspaceNameSuccessful, updateWorkspaceTagsSuccessful, setDeleteTarget, loadWorkspaceLayoutSuccessful, clearCalculationTabularResults, setOnePanelView, setPanelForceExpanded, clearCalculationLinegraphResults, clearCalculationHeatmapResults, clearAllResults, exportResults, exportResultsFinish, setCalculationMetadataResults, clearCalculationMetadataResults, setProtectedSimpleMetric, goBackHandle, setPersistPanelsState, setDuplicatedResultSetId, setMetricWithHighlightedElements, setConfigurationWithHighlightedElements, resetHighlightedElements, setMetricTypeWithHighlightedElements, scenarioExport, scenarioExportFinish, addSocketPendingOperation, removeSocketPendingOperation, resetSocketPendingOperations, socketReconnecting, setResultElementWithLongCalculation, removeResultElementsWithLongCalculation, setShouldScrollToResultElement, setWorkspaceServerWarning, } from "./ps-workspace.actions";
import { filterResultElementsWithLongCalculation, prepareCalculationTabularResultEntry, prepareGraphResultsState, prepareStateOnRemoveStoredGraphItem, prepareWorkspace, prepareWorkspaceLayout, } from "./ps-workspace.utils";
import { EngineResultType } from "./ps-workspace.types";
const MAX_PENDING_SOCKET_OPERATIONS_STORED = 30;
export default createReducer(initialState, (builder) => {
    builder
        .addCase(subscribeWorkspaceSuccessful, (state) => {
        state.isSubscribed = true;
    })
        .addCase(unsubscribeWorkspace, (state) => {
        state.isSubscribed = false;
    })
        .addCase(goBackHandle, () => initialState)
        .addCase(socketReconnecting, (state) => {
        state.isSubscribed = false;
    })
        .addCase(loadWorkspace, () => initialState)
        .addCase(loadWorkspaceSuccessful, (state, { payload }) => {
        const workspace = prepareWorkspace(payload.workspace || {});
        const workspaceExpanded = { ...payload, workspace };
        const workspaceLayout = prepareWorkspaceLayout(workspace, state.layout, state.duplicatedResultSetId);
        return {
            ...state,
            ...workspaceExpanded,
            duplicatedResultSetId: null,
            loading: false,
            layout: workspaceLayout.length > 0 ? workspaceLayout : state.layout,
        };
    })
        .addCase(loadWorkspaceLayoutSuccessful, (state, { payload }) => {
        state.layout = payload;
    })
        .addCase(updateWorkspaceLayout, (state, { payload }) => {
        const layoutsToKeep = state.layout?.filter((item) => item.id !== payload.id) || [];
        state.layout = [...layoutsToKeep, payload];
    })
        .addCase(setSocketStatus, (state, { payload }) => {
        state.socketStatus = payload;
    })
        .addCase(addSocketPendingOperation, (state, { payload }) => {
        if (state.pendingSocketOperations.length === MAX_PENDING_SOCKET_OPERATIONS_STORED) {
            state.pendingSocketOperations.shift();
        }
        state.pendingSocketOperations.push(payload);
    })
        .addCase(removeSocketPendingOperation, (state, { payload }) => {
        const removedItem = state.pendingSocketOperations.find((item) => item.requestId === payload);
        if (!removedItem) {
            return;
        }
        const indexOfRemovedItem = state.pendingSocketOperations.indexOf(removedItem);
        state.pendingSocketOperations.splice(indexOfRemovedItem, 1);
    })
        .addCase(resetSocketPendingOperations, (state) => {
        state.pendingSocketOperations = [];
    })
        .addCase(setSocketError, (state, { payload }) => {
        state.lastSocketErrors = payload;
        const removedItem = state.pendingSocketOperations.find((item) => item.requestId === payload?.requestId);
        if (!removedItem) {
            return;
        }
        const indexOfRemovedItem = state.pendingSocketOperations.indexOf(removedItem);
        state.pendingSocketOperations.splice(indexOfRemovedItem, 1);
    })
        .addCase(updateWorkspaceTagsSuccessful, (state, { payload }) => {
        state.tags = payload;
        state.tagsSaving = false;
    })
        .addCase(updateWorkspaceName, (state) => {
        state.renameWorkspaceInProgress = true;
    })
        .addCase(updateWorkspaceNameSuccessful, (state, { payload }) => {
        state.name = payload;
        state.serverError = null;
        state.renameWorkspaceInProgress = false;
    })
        .addCase(updateWorkspaceNameFailure, (state, { payload }) => {
        state.serverError = payload;
        state.renameWorkspaceInProgress = false;
    })
        .addCase(loadWorkspaceFailure, (state) => {
        state.loading = false;
    })
        .addCase(createWorkspace, (state) => {
        state.saving = true;
        state.serverError = null;
    })
        .addCase(createWorkspaceFailure, (state) => {
        state.saving = false;
    })
        .addCase(createWorkspaceSuccessful, () => initialState)
        .addCase(setWorkspaceServerWarning, (state, { payload }) => {
        state.serverWarning = payload;
    })
        .addCase(setWorkspaceServerError, (state, { payload }) => {
        state.serverError = payload;
    })
        .addCase(setScenarioId, (state, { payload }) => {
        if (payload === null) {
            state.currentScenarioId = null;
        }
        else {
            state.currentScenarioId = payload.id;
            if (payload.isNew) {
                state.scenarioEntityCreating = true;
                state.currentScenarioEntity = null;
            }
        }
    })
        .addCase(setScenarioEntity, (state, { payload }) => {
        if (payload === null) {
            state.currentScenarioEntity = null;
        }
        else {
            state.currentScenarioEntity = { id: payload.id, type: payload.type };
            if (payload.isNew) {
                state.scenarioEntityCreating = true;
            }
        }
    })
        .addCase(setResultSetId, (state, { payload }) => {
        if (payload === null) {
            state.currentResultSetId = null;
        }
        else {
            state.currentResultSetId = payload.id;
            if (payload.isNew) {
                state.resultEntityCreating = true;
                state.currentResultSetElement = null;
            }
        }
    })
        .addCase(setResultSetElement, (state, { payload }) => {
        if (payload === null) {
            state.currentResultSetElement = null;
            state.resultEntityCreating = false;
        }
        else {
            state.currentResultSetElement = { id: payload.id, type: payload.type };
            if (payload.isNew) {
                state.resultEntityCreating = true;
                state.onePanelView = true;
            }
        }
    })
        .addCase(setCalculationTabularResults, (state, { payload }) => {
        const filtered = state.calculationTabularResults.filter(({ resultSetId, resultSetElementId, recordId }) => resultSetId !== payload.resultSetId ||
            resultSetElementId !== payload.resultSetElementId ||
            recordId !== payload.recordId);
        filtered.push(prepareCalculationTabularResultEntry(payload));
        state.calculationTabularResults = filtered;
    })
        .addCase(clearCalculationTabularResults, (state, { payload }) => {
        const filtered = state.calculationTabularResults.filter(({ resultSetId, resultSetElementId, recordId }) => resultSetId !== payload.resultSetId ||
            resultSetElementId !== payload.resultSetElementId ||
            (payload.recordId !== undefined && recordId !== payload.recordId));
        state.calculationTabularResults = filtered;
    })
        .addCase(setCalculationLineGraphResults, (state, { payload }) => {
        state.calculationLineGraphResults = prepareGraphResultsState(state.calculationLineGraphResults, payload);
    })
        .addCase(clearCalculationLinegraphResults, (state, { payload }) => {
        const filtered = state.calculationLineGraphResults.filter(({ resultSetId, resultSetElementId }) => resultSetId !== payload.resultSetId || resultSetElementId !== payload.resultSetElementId);
        state.calculationLineGraphResults = filtered;
    })
        .addCase(setCalculationHeatmapResults, (state, { payload }) => {
        state.calculationHeatmapResults = prepareGraphResultsState(state.calculationHeatmapResults, payload);
    })
        .addCase(clearCalculationHeatmapResults, (state, { payload }) => {
        const filtered = state.calculationHeatmapResults.filter(({ resultSetId, resultSetElementId }) => resultSetId !== payload.resultSetId || resultSetElementId !== payload.resultSetElementId);
        state.calculationHeatmapResults = filtered;
    })
        .addCase(setCalculationMetadataResults, (state, { payload }) => {
        const filtered = state.calculationMetadataResults.filter(({ resultSetId, resultSetElementId, recordId }) => resultSetId !== payload.resultSetId ||
            resultSetElementId !== payload.resultSetElementId ||
            recordId !== payload.recordId);
        filtered.push(payload);
        state.calculationMetadataResults = filtered;
    })
        .addCase(clearCalculationMetadataResults, (state, { payload }) => {
        const filtered = state.calculationMetadataResults.filter(({ resultSetId, resultSetElementId, recordId }) => resultSetId !== payload.resultSetId ||
            resultSetElementId !== payload.resultSetElementId ||
            (payload.recordId !== undefined && recordId !== payload.recordId));
        state.calculationMetadataResults = filtered;
    })
        .addCase(clearAllResults, (state, { payload }) => {
        const filteredTabularResults = state.calculationTabularResults.filter(({ resultSetId }) => resultSetId !== payload);
        const filteredLinegraphResults = state.calculationLineGraphResults.filter(({ resultSetId }) => resultSetId !== payload);
        const filteredHeatmapResults = state.calculationHeatmapResults.filter(({ resultSetId }) => resultSetId !== payload);
        const filteredMetadataResults = state.calculationMetadataResults.filter(({ resultSetId }) => resultSetId !== payload);
        state.calculationTabularResults = filteredTabularResults;
        state.calculationLineGraphResults = filteredLinegraphResults;
        state.calculationHeatmapResults = filteredHeatmapResults;
        state.calculationMetadataResults = filteredMetadataResults;
    })
        .addCase(removeStoredGraphItem, (state, { payload }) => {
        if (payload.type === EngineResultType.Result1D) {
            state.calculationLineGraphResults = prepareStateOnRemoveStoredGraphItem(state.calculationLineGraphResults, payload);
        }
        else if (payload.type === EngineResultType.Result2D) {
            state.calculationHeatmapResults = prepareStateOnRemoveStoredGraphItem(state.calculationHeatmapResults, payload);
        }
    })
        .addCase(getMetricTemplateAnnotationsSuccessful, (state, { payload }) => {
        state.templateAnnotationsGraphResults = payload;
    })
        .addCase(resetMetricTemplateAnnotations, (state) => {
        state.templateAnnotationsGraphResults = null;
    })
        .addCase(unlinkTags, (state) => {
        state.tagsSaving = true;
    })
        .addCase(linkTags, (state) => {
        state.tagsSaving = true;
    })
        .addCase(linkTagsFailure, (state) => {
        state.tagsSaving = false;
    })
        .addCase(unlinkTagsFailure, (state) => {
        state.tagsSaving = false;
    })
        .addCase(scenarioEntityCreatingReset, (state) => {
        state.scenarioEntityCreating = false;
    })
        .addCase(resultEntityCreatingReset, (state) => {
        state.resultEntityCreating = false;
    })
        .addCase(setPrintCanvasContent, (state, { payload }) => {
        state.printCanvasContent = payload;
    })
        .addCase(removePrintCanvasContent, (state) => {
        state.printCanvasContent = null;
    })
        .addCase(setDeleteTarget, (state, { payload }) => {
        state.deleteTarget = payload;
    })
        .addCase(setOnePanelView, (state, { payload }) => {
        state.onePanelView = payload;
    })
        .addCase(setPanelForceExpanded, (state, { payload }) => {
        state.panelForceExpanded = payload;
    })
        .addCase(setPersistPanelsState, (state, { payload }) => {
        state.shouldPersistPanelsState = payload;
    })
        .addCase(setDuplicatedResultSetId, (state, { payload }) => {
        state.duplicatedResultSetId = payload;
    })
        .addCase(setMetricWithHighlightedElements, (state, { payload }) => {
        state.metricIdWithHighlightedElements = payload;
    })
        .addCase(setMetricTypeWithHighlightedElements, (state, { payload }) => {
        state.metricTypeWithHighlightedElements = payload;
    })
        .addCase(setConfigurationWithHighlightedElements, (state, { payload }) => {
        state.configurationIdWithHighlightedElements = payload;
    })
        .addCase(resetHighlightedElements, (state) => {
        state.metricIdWithHighlightedElements = null;
        state.configurationIdWithHighlightedElements = null;
        state.metricTypeWithHighlightedElements = null;
    })
        .addCase(exportResults, (state) => {
        state.exportInProgress = true;
    })
        .addCase(exportResultsFinish, (state) => {
        state.exportInProgress = false;
    })
        .addCase(scenarioExport, (state) => {
        state.scenarioExportInProgress = true;
    })
        .addCase(scenarioExportFinish, (state) => {
        state.scenarioExportInProgress = false;
    })
        .addCase(setProtectedSimpleMetric, (state, { payload }) => {
        state.protectedSimpleMetricAttachments = payload;
    })
        .addCase(setShouldScrollToResultElement, (state, { payload }) => {
        state.shouldScrollToResultElement = payload;
    })
        .addCase(setResultElementWithLongCalculation, (state, { payload }) => {
        state.resultElementsWithLongCalculation.push(payload);
    })
        .addCase(removeResultElementsWithLongCalculation, (state, { payload }) => {
        state.resultElementsWithLongCalculation = filterResultElementsWithLongCalculation(state.resultElementsWithLongCalculation, payload);
    });
});
